import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage, firestore } from '../../firebase/config';
import { doc, setDoc, deleteDoc, getDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Job Card Component
 * 
 * A reusable card component for displaying job information
 * 
 * @param {Object} props Component props
 * @param {string} props.id - Job ID
 * @param {string} props.title - Job title
 * @param {string} props.businessName - Name of the business
 * @param {string} props.businessLogo - URL to the business logo
 * @param {string} props.businessImagePath - Path to the business image in Firebase storage
 * @param {string} props.location - Job location
 * @param {string} props.salary - Job salary information
 * @param {number} props.postedDays - Days since the job was posted
 * @param {string} props.description - Job description
 * @param {string} props.address - Job address
 * @param {string} props.pay - Payment details
 * @param {function} props.onApply - Function to handle job application
 * @param {string} props.businessId - ID of the business
 * @param {boolean} props.immediateStart - Whether the job offers immediate start
 * @param {string} props.applicationUrl - External application URL if available
 */
const JobCard = ({ 
  id,
  title, 
  businessName, 
  businessLogo, 
  businessImagePath,
  location, 
  salary, 
  postedDays, 
  description, 
  address, 
  pay,
  onApply,
  businessId,
  immediateStart = false,
  applicationUrl = null
}) => {
  const { currentUser } = useAuth();
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isCheckingFavorite, setIsCheckingFavorite] = useState(true);

  // Fetch the image URL from Firebase Storage if we have a path
  useEffect(() => {
    const fetchImage = async () => {
      if (businessImagePath) {
        try {
          console.log(`Fetching business image from path: ${businessImagePath}`);
          setImageLoading(true);
          const imageRef = ref(storage, businessImagePath);
          const url = await getDownloadURL(imageRef);
          console.log(`Successfully fetched image URL: ${url}`);
          setImageUrl(url);
          setImageError(false);
        } catch (error) {
          console.error(`Error fetching image from path ${businessImagePath}:`, error);
          setImageError(true);
        } finally {
          setImageLoading(false);
        }
      } else if (businessLogo) {
        // If we have a direct URL, use it
        setImageUrl(businessLogo);
        setImageLoading(false);
      } else {
        setImageLoading(false);
        setImageError(true);
      }
    };

    fetchImage();
  }, [businessImagePath, businessLogo]);

  // Check if job is in user's favorites
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!currentUser) {
        setIsCheckingFavorite(false);
        return;
      }
      
      try {
        const favoriteRef = doc(firestore, 'favorites', `${currentUser.uid}_${id}`);
        const favoriteDoc = await getDoc(favoriteRef);
        setIsFavorite(favoriteDoc.exists());
      } catch (error) {
        console.error('Error checking favorite status:', error);
      } finally {
        setIsCheckingFavorite(false);
      }
    };

    checkFavoriteStatus();
  }, [currentUser, id]);

  // Generate initials for fallback (up to 2 letters)
  const businessInitials = businessName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  const formatPostedDate = () => {
    if (postedDays === 0) return 'Today';
    if (postedDays === 1) return 'Yesterday';
    return `${postedDays} days ago`;
  };

  const handleToggleFavorite = async () => {
    if (!currentUser) {
      // Redirect to login or show login prompt
      alert('Please log in to save jobs to favorites');
      return;
    }

    try {
      const favoriteId = `${currentUser.uid}_${id}`;
      const favoriteRef = doc(firestore, 'favorites', favoriteId);

      if (isFavorite) {
        // Remove from favorites
        await deleteDoc(favoriteRef);
        setIsFavorite(false);
      } else {
        // Add to favorites
        const timestamp = new Date();
        await setDoc(favoriteRef, {
          userId: currentUser.uid,
          jobId: id,
          createdAt: timestamp,
          updatedAt: timestamp
        });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      alert('Failed to update favorites. Please try again.');
    }
  };

  const handleApply = async () => {
    if (applicationUrl) {
      // Open external application URL in a new tab
      window.open(applicationUrl, '_blank');
      return;
    }

    if (!currentUser) {
      alert('Please log in to apply for this job');
      return;
    }

    if (onApply) {
      // Call the provided onApply function first
      onApply(id);
    }

    try {
      // Create a unique ID for the application
      const applicationId = `${currentUser.uid}_${id}`;
      const applicationRef = doc(firestore, 'jobApplications', applicationId);
      
      // Check if user has already applied for this job
      const applicationDoc = await getDoc(applicationRef);
      
      if (applicationDoc.exists()) {
        alert('You have already applied for this job');
        return;
      }

      // Add application to database with all required fields
      await setDoc(applicationRef, {
        id: applicationId,
        userId: currentUser.uid,
        jobId: id,
        businessId: businessId, // Include businessId field
        status: 'pending',
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

              // alert('Application submitted successfully!'); // Disabled notification
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  const handleCardClick = (e) => {
    // Don't trigger for child links or buttons that have their own handlers
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }
    // Open job detail page in a new tab
    window.open(`/dashboard/jobs/${id}`, '_blank');
  };

  return (
    <div 
      className="bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-[#FF8C00]/30 hover:shadow-md transition-all duration-300 group relative flex flex-col h-full max-w-[280px] cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Header with business info */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {/* Business logo - increased size from 10 to 14 */}
          <Link to={`/dashboard/businesses/${businessId}`} className="block flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-md overflow-hidden flex items-center justify-center border border-gray-100 shadow-sm bg-white">
              {imageLoading ? (
                <div className="animate-pulse bg-gray-200 w-full h-full rounded-md"></div>
              ) : imageUrl && !imageError ? (
                <img 
                  src={imageUrl} 
                  alt={`${businessName} logo`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.log("Business logo failed to load:", e.target.src);
                    e.target.onerror = null;
                    setImageError(true);
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF8C00]/20 to-[#FF8C00]/10 text-[#FF8C00] text-sm font-bold">
                  {businessInitials}
                </div>
              )}
            </div>
          </Link>
          
          <div className="min-w-0 flex-1">
            <Link to={`/dashboard/businesses/${businessId}`} className="block w-full" onClick={(e) => e.stopPropagation()}>
              <p className="text-gray-700 font-medium hover:text-[#FF8C00] transition-colors text-base line-clamp-2 text-left">{businessName}</p>
            </Link>
            <div className="flex items-center text-gray-500 text-xs">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                   className="h-3 w-3 mr-1 stroke-2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span className="text-[11px]">{formatPostedDate()}</span>
            </div>
          </div>
        </div>

        {/* Immediate Start badge - commented out as requested */}
        {/* {immediateStart && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FF8C00]/10 text-[#FF8C00] border border-[#FF8C00]/20 whitespace-nowrap">
            Immediate
          </span>
        )} */}
      </div>

      {/* Job content */}
      <div className="p-3 flex-grow flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <Link to={`/dashboard/jobs/${id}`} className="inline-block">
            <h3 className="text-base font-bold text-gray-800 group-hover:text-[#FF8C00] transition-colors line-clamp-1 text-left">{title}</h3>
          </Link>
          
          {/* Favorite/Like button */}
          <button 
            onClick={handleToggleFavorite}
            className="flex-shrink-0 ml-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            {isCheckingFavorite ? (
              <div className="animate-pulse bg-gray-200 w-5 h-5 rounded-full"></div>
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill={isFavorite ? "currentColor" : "none"} 
                stroke="currentColor" 
                className={`h-5 w-5 transition-colors ${isFavorite ? 'text-[#FF8C00] fill-[#FF8C00]' : 'text-gray-400'}`}
                strokeWidth={isFavorite ? "0" : "2"}
              >
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
              </svg>
            )}
          </button>
        </div>

        <p className="text-gray-600 line-clamp-3 text-xs mb-3 text-left">{description}</p>

        <div className="mt-auto space-y-2">
          {(address || location) && (
            <div className="flex items-center text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                   className="h-4 w-4 mr-1.5 text-gray-500 stroke-2 flex-shrink-0">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              <span className="font-medium truncate text-xs text-left">{address || location}</span>
            </div>
          )}
          {(pay || salary) && (
            <div className="flex items-center text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" 
                   className="h-4 w-4 mr-1.5 text-gray-500 stroke-2 flex-shrink-0">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <span className="font-medium text-xs text-left">{pay || salary}</span>
            </div>
          )}
        </div>
      </div>

      {/* Apply button */}
      <div className="p-3 pt-2 border-t border-gray-100">
        <button 
          onClick={handleApply}
          className="w-full py-2 text-sm font-medium bg-[#FF8C00] text-white rounded-md hover:bg-[#FF8C00]/90 transition-all shadow-sm hover:shadow transform hover:-translate-y-0.5"
        >
          {applicationUrl ? 'Apply on Website' : 'Apply Now'}
        </button>
      </div>
    </div>
  );
};

export default JobCard; 